import React, { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Camera, ArrowLeftRight, CalendarDays, Sparkles, Loader2, Lock, Crown, ScanFace } from 'lucide-react';
import { logError } from '../lib/errorLogger';
import { toast } from 'sonner';
import axios from 'axios';
import ImageSliderCompare from './ImageSliderCompare';
import AcneTrendChart from './AcneTrendChart';
import SkinReportCard from './SkinReportCard';
import ShareProgressCard from './ShareProgressCard';
import FaceScanModal from './FaceScanModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyzeCard = ({ onScanClick, onSelfieUpload, loading }) => (
  <Card className="card-elevated border-0 overflow-hidden" data-testid="analyze-card">
    <div className="bg-gradient-to-br from-[#FF6B4A] to-[#E8552F] p-5 text-white">
      <div className="flex items-center gap-2 mb-1">
        <ScanFace className="w-5 h-5 text-[#4ADE80]" />
        <h3 className="font-heading text-lg font-bold">Analyze My Skin</h3>
      </div>
      <p className="text-xs text-white/70">Snap a selfie for an instant clinical AI skin report</p>
    </div>
    <CardContent className="p-5 space-y-3">
      <Button onClick={onScanClick} disabled={loading} className="w-full bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full h-12 text-base font-semibold" data-testid="open-face-scan-btn">
        <Camera className="w-5 h-5 mr-2" /> Scan My Skin
      </Button>
      <label className="block text-center cursor-pointer" data-testid="selfie-upload">
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={onSelfieUpload} className="hidden" disabled={loading} />
        <span className="text-sm text-[#8C7A76] hover:text-[#FF6B4A] transition-colors underline underline-offset-2">
          {loading ? 'Uploading…' : 'or upload a photo'}
        </span>
      </label>
    </CardContent>
  </Card>
);

const SelfieTimelineItem = ({ selfie, isSelected, onSelect, selectionMode }) => {
  const dateStr = new Date(selfie.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return (
    <div
      className={`p-4 rounded-xl cursor-pointer transition-all duration-300 ${
        isSelected ? 'bg-[#FF6B4A] text-white ring-2 ring-[#FF6B4A]' : 'bg-[#FFF2EC] hover:bg-[#E5D0C9]'
      }`}
      onClick={() => onSelect(selfie)}
      data-testid={`selfie-item-${selfie.selfie_id}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CalendarDays className={`w-4 h-4 ${isSelected ? 'text-white/70' : 'text-[#8C7A76]'}`} />
          <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-[#1A1514]'}`}>{dateStr}</span>
        </div>
        {selectionMode && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20' : 'bg-[#4ADE80]/15 text-[#FF6B4A]'}`}>
            {isSelected ? 'Selected' : 'Select'}
          </span>
        )}
      </div>
      {selfie.analysis?.overall_score != null && (
        <div className="flex items-center gap-1.5 mb-1">
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-[#FF6B4A]/10 text-[#FF6B4A]'}`}>
            {selfie.analysis.overall_score}/100
          </span>
          {selfie.analysis.skin_age != null && (
            <span className={`text-xs ${isSelected ? 'text-white/70' : 'text-[#8C7A76]'}`}>skin age {selfie.analysis.skin_age}</span>
          )}
        </div>
      )}
      {selfie.analysis?.raw_analysis && (
        <p className={`text-xs line-clamp-2 ${isSelected ? 'text-white/80' : 'text-[#8C7A76]'}`}>
          {selfie.analysis.raw_analysis.substring(0, 100)}...
        </p>
      )}
    </div>
  );
};

const CompareView = ({ comparison, loadingCompare }) => {
  if (!comparison && !loadingCompare) return null;

  const beforeDate = comparison?.before?.date ? new Date(comparison.before.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  const afterDate = comparison?.after?.date ? new Date(comparison.after.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

  return (
    <div className="space-y-4 animate-fade-in" data-testid="compare-view">
      {/* Image slider overlay */}
      {comparison?.before?.image && comparison?.after?.image && (
        <ImageSliderCompare
          beforeImage={comparison.before.image}
          afterImage={comparison.after.image}
          beforeDate={beforeDate}
          afterDate={afterDate}
        />
      )}

      {/* Text analysis side-by-side */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-[#FFF2EC] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-[#8C7A76] uppercase tracking-wider">Before</span>
            <span className="text-xs text-[#8C7A76]">{beforeDate}</span>
          </div>
          <p className="text-sm text-[#1A1514] leading-relaxed">
            {comparison?.before?.analysis_summary || 'No analysis available'}
          </p>
        </div>
        <div className="bg-[#FFF2EC] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-[#4ADE80] uppercase tracking-wider">After</span>
            <span className="text-xs text-[#8C7A76]">{afterDate}</span>
          </div>
          <p className="text-sm text-[#1A1514] leading-relaxed">
            {comparison?.after?.analysis_summary || 'No analysis available'}
          </p>
        </div>
      </div>

      {/* AI comparison */}
      {loadingCompare && (
        <div className="bg-gradient-to-r from-[#4ADE80]/10 to-[#FF6B4A]/5 rounded-xl p-5 text-center">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-[#FF6B4A]" />
          <p className="text-sm text-[#8C7A76]">Generating AI comparison...</p>
        </div>
      )}
      {comparison?.ai_comparison && (
        <div className="bg-gradient-to-r from-[#4ADE80]/10 to-[#FF6B4A]/5 rounded-xl p-5" data-testid="ai-comparison">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#FF6B4A]" />
            <span className="text-sm font-semibold text-[#1A1514]">AI Progress Analysis</span>
          </div>
          <p className="text-sm text-[#1A1514] leading-relaxed">{comparison.ai_comparison}</p>
        </div>
      )}
    </div>
  );
};

const SkinProgressSection = ({ selfies, onSelfieUpload, onSelfieCapture, loading, subscription, onUpgrade }) => {
  const isPremium = subscription === 'premium';
  const FREE_SELFIE_LIMIT = 3;
  const [mode, setMode] = useState('gallery');
  const [scanOpen, setScanOpen] = useState(false);
  const [beforeSelfie, setBeforeSelfie] = useState(null);
  const [afterSelfie, setAfterSelfie] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);
  const [selectedSelfie, setSelectedSelfie] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch the full-resolution image for the selected selfie (for the on-photo overlay)
  useEffect(() => {
    setSelectedImage(null);
    const id = selectedSelfie?.selfie_id;
    if (!id || !selectedSelfie?.analysis?.zone_scores) return;
    let cancelled = false;
    axios.get(`${API}/selfies/${id}`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const img = res.data?.full_image;
        if (img) setSelectedImage(img.startsWith('data:') ? img : `data:image/jpeg;base64,${img}`);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedSelfie]);

  // Auto-select the most recent selfie for the report in gallery mode
  useEffect(() => {
    if (mode !== 'gallery') return;
    if (selfies.length === 0) {
      setSelectedSelfie(null);
      return;
    }
    setSelectedSelfie((prev) => {
      const stillExists = prev && selfies.some((s) => s.selfie_id === prev.selfie_id);
      return stillExists ? prev : selfies[0];
    });
  }, [selfies, mode]);

  const fetchComparison = useCallback(async (beforeId, afterId) => {
    setLoadingCompare(true);
    try {
      const res = await axios.get(`${API}/selfies/compare/${beforeId}/${afterId}`, { withCredentials: true });
      setComparison(res.data);
    } catch (err) {
      logError('SkinProgress', 'Failed to fetch comparison', { error: err?.message });
      toast.error('Could not generate comparison.');
    } finally {
      setLoadingCompare(false);
    }
  }, []);

  const handleSelectSelfie = useCallback((selfie) => {
    if (mode !== 'compare') {
      setSelectedSelfie(selfie);
      return;
    }
    if (!beforeSelfie) {
      setBeforeSelfie(selfie);
      setAfterSelfie(null);
      setComparison(null);
    } else if (!afterSelfie && selfie.selfie_id !== beforeSelfie.selfie_id) {
      setAfterSelfie(selfie);
      fetchComparison(beforeSelfie.selfie_id, selfie.selfie_id);
    } else {
      setBeforeSelfie(selfie);
      setAfterSelfie(null);
      setComparison(null);
    }
  }, [mode, beforeSelfie, afterSelfie, fetchComparison]);

  const enterCompareMode = () => {
    setMode('compare');
    setBeforeSelfie(null);
    setAfterSelfie(null);
    setComparison(null);
  };

  const exitCompareMode = () => {
    setMode('gallery');
    setBeforeSelfie(null);
    setAfterSelfie(null);
    setComparison(null);
  };

  const hasEnoughSelfies = selfies.length >= 2;

  return (
    <div className="space-y-6">
      {/* Trend Chart */}
      <AcneTrendChart />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left column */}
        <div className="lg:col-span-4 space-y-6">
          {/* Upload - limited for free users */}
          {!isPremium && selfies.length >= FREE_SELFIE_LIMIT ? (
            <Card className="card-elevated border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#1A1514]">
                  <Camera className="w-5 h-5 text-[#FF6B4A]" /> Upload Selfie
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6">
                  <Lock className="w-10 h-10 text-[#A388EE]/50 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-[#1A1514]">Upload limit reached ({FREE_SELFIE_LIMIT}/{FREE_SELFIE_LIMIT})</p>
                  <p className="text-xs text-[#8C7A76] mt-1 mb-4">Upgrade to Premium for unlimited selfie tracking</p>
                  <button onClick={onUpgrade} className="inline-flex items-center gap-1.5 bg-[#FF6B4A] hover:bg-[#E8552F] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors" data-testid="selfie-upgrade-btn">
                    <Crown className="w-3.5 h-3.5" /> Upgrade
                  </button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <AnalyzeCard onScanClick={() => setScanOpen(true)} onSelfieUpload={onSelfieUpload} loading={loading} />
          )}

          {hasEnoughSelfies && (
            <Card className="card-elevated border-0" data-testid="compare-toggle-card">
              <CardContent className="p-5">
                {mode === 'gallery' ? (
                  <Button onClick={enterCompareMode} className="w-full bg-[#FF6B4A] hover:bg-[#E8552F] rounded-xl" data-testid="compare-mode-btn">
                    <ArrowLeftRight className="w-4 h-4 mr-2" /> Compare Before & After
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#1A1514]">Compare Mode</span>
                      <Button variant="ghost" size="sm" onClick={exitCompareMode} className="text-xs text-[#8C7A76]" data-testid="exit-compare-btn">Exit</Button>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${beforeSelfie ? 'bg-[#FF6B4A]' : 'bg-[#E5D0C9]'}`} />
                        <span className="text-xs text-[#8C7A76]">
                          {beforeSelfie ? `Before: ${new Date(beforeSelfie.created_at).toLocaleDateString()}` : 'Select a "Before" selfie'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${afterSelfie ? 'bg-[#4ADE80]' : 'bg-[#E5D0C9]'}`} />
                        <span className="text-xs text-[#8C7A76]">
                          {afterSelfie ? `After: ${new Date(afterSelfie.created_at).toLocaleDateString()}` : 'Select an "After" selfie'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right column */}
        <div className="lg:col-span-8 space-y-6">
          {mode === 'compare' && (beforeSelfie && afterSelfie) && (
            <>
              <CompareView comparison={comparison} loadingCompare={loadingCompare} />
              {comparison && (
                <ShareProgressCard
                  comparison={comparison}
                  beforeAnalysis={beforeSelfie.analysis}
                  afterAnalysis={afterSelfie.analysis}
                />
              )}
            </>
          )}

          {mode === 'gallery' && selectedSelfie && (
            <SkinReportCard
              selfie={selectedSelfie}
              imageSrc={selectedImage}
              prevAnalysis={
                (() => {
                  const idx = selfies.findIndex((s) => s.selfie_id === selectedSelfie.selfie_id);
                  const prev = idx >= 0 ? selfies.slice(idx + 1).find((s) => s.analysis?.scores) : null;
                  return prev?.analysis || null;
                })()
              }
              isPremium={isPremium}
              onUpgrade={onUpgrade}
            />
          )}

          <Card className="card-elevated border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#1A1514]">
                  {mode === 'compare' ? 'Select Selfies to Compare' : 'Skin Progress Timeline'}
                </CardTitle>
                {mode === 'compare' && !beforeSelfie && (
                  <span className="text-xs text-[#A388EE] bg-[#A388EE]/10 px-3 py-1 rounded-full animate-pulse">Pick your "Before"</span>
                )}
                {mode === 'compare' && beforeSelfie && !afterSelfie && (
                  <span className="text-xs text-[#4ADE80] bg-[#4ADE80]/10 px-3 py-1 rounded-full animate-pulse">Now pick your "After"</span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {selfies.length > 0 ? (
                <ScrollArea className="h-[350px]">
                  <div className="space-y-3">
                    {selfies.map((selfie) => (
                      <SelfieTimelineItem
                        key={selfie.selfie_id}
                        selfie={selfie}
                        isSelected={
                          mode === 'compare'
                            ? (selfie.selfie_id === beforeSelfie?.selfie_id || selfie.selfie_id === afterSelfie?.selfie_id)
                            : selfie.selfie_id === selectedSelfie?.selfie_id
                        }
                        onSelect={handleSelectSelfie}
                        selectionMode={mode === 'compare'}
                      />
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12">
                  <ScanFace className="w-16 h-16 mx-auto text-[#E5D0C9] mb-4" />
                  <p className="text-[#8C7A76] mb-1">No scans yet</p>
                  <p className="text-xs text-[#8C7A76] mb-4">Scan your face to get an instant clinical AI skin report</p>
                  <Button onClick={() => setScanOpen(true)} className="bg-[#FF6B4A] hover:bg-[#E8552F] rounded-full" data-testid="empty-scan-btn">
                    <Camera className="w-4 h-4 mr-2" /> Scan My Skin
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <FaceScanModal open={scanOpen} onOpenChange={setScanOpen} onCapture={onSelfieCapture} />
    </div>
  );
};

export default SkinProgressSection;
